import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import _ from "@lodash";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useEffect, useState } from "react";
import jwtService from "../../auth/services/jwtService";
import { showMessage } from "app/store/fuse/messageSlice";
import { useDispatch } from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  email: yup
    .string()
    .email("You must enter a valid email")
    .required("You must enter a email"),
  password: yup
    .string()
    .required("Please enter your password.")
    .min(4, "Password is too short - must be at least 4 chars."),
});

const defaultValues = {
  email: "",
  password: "",
  remember: true,
};

function SignInPage() {
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const dispatch = useDispatch();
  const { control, formState, handleSubmit, setError, setValue } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const navigate = useNavigate()

  const { isValid, dirtyFields, errors } = formState;

  // useEffect(() => {
  //   setValue('email', '', { shouldDirty: true, shouldValidate: true });
  //   setValue('password', '', { shouldDirty: true, shouldValidate: true });
  // }, [setValue]);

  function onSubmit({ email, password }) {
    window.localStorage.removeItem('jwt_user_imp');
    setLoadingSignIn(true);
    jwtService
      .signInWithEmailAndPassword(email, password)
      .then((user) => {
        setLoadingSignIn(false);
        // No need to do anything, user data will be set at app/auth/AuthContext
      })
      .catch((_errors) => {
        setLoadingSignIn(false);
        _errors.forEach((error) => {
          if (error?.type == "blocked") {
            dispatch(showMessage({ message: error.message, variant: "error" }));
          } else if (error?.type == "deleted") {
            dispatch(showMessage({ message: error.message, variant: "error" }));
          } else if (error?.type == "2Fa Verify" || error?.type == "2Fa") {
            navigate('/auth/method', { state: { email, password, type: error?.type, data: error?.data } });
          } else {
            setError(error.type, {
              type: "manual",
              message: error.message,
            });
          }
        });
      });
  }

  return (
    <div className="flex flex-col items-center flex-auto min-w-0 sm:justify-center md:p-32">
      <Paper className="flex w-full min-h-full overflow-hidden sm:w-auto sm:min-h-auto md:w-full md:max-w-6xl rounded-0 sm:rounded-2xl sm:shadow">
        <div className="w-full px-16 py-32 sm:w-auto sm:p-48 md:p-64 ltr:border-r-1 rtl:border-l-1">
          <div className="w-full mx-auto max-w-320 sm:w-320 sm:mx-0">
            <img
              className="w-48"
              src="assets/images/logo/logo.svg"
              alt="logo"
            />

            <Typography className="mt-32 text-4xl font-extrabold leading-tight tracking-tight">
              Sign in
            </Typography>
            <div className="flex items-baseline mt-2 font-medium"></div>

            <form
              name="loginForm"
              noValidate
              className="flex flex-col justify-center w-full mt-32"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-24"
                    label="Email"
                    autoFocus
                    type="email"
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                    variant="outlined"
                    required
                    fullWidth
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-24"
                    label="Password"
                    type="password"
                    error={!!errors.password}
                    helperText={errors?.password?.message}
                    variant="outlined"
                    required
                    fullWidth
                  />
                )}
              />

              <div className="flex flex-col items-center justify-center sm:flex-row sm:justify-between">
                <Controller
                  name="remember"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormControlLabel
                        label="Remember me"
                        control={<Checkbox size="small" {...field} />}
                      />
                    </FormControl>
                  )}
                />

                <Link
                  className="font-medium text-md"
                  to="/forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <LoadingButton
                loading={loadingSignIn}
                variant="contained"
                color="secondary"
                className="w-full mt-16 "
                aria-label="Sign in"
                disabled={_.isEmpty(dirtyFields) || !isValid}
                type="submit"
                size="large"
              >
                Sign in
              </LoadingButton>
            </form>
          </div>
        </div>

        <Box
          className="relative items-center justify-center flex-auto hidden h-full p-64 overflow-hidden md:flex lg:px-112"
          sx={{ backgroundColor: "primary.main" }}
        >
          <svg
            className="absolute inset-0 pointer-events-none"
            viewBox="0 0 960 540"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMax slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            <Box
              component="g"
              sx={{ color: "primary.light" }}
              className="opacity-20"
              fill="none"
              stroke="currentColor"
              strokeWidth="100"
            >
              <circle r="234" cx="196" cy="23" />
              <circle r="234" cx="790" cy="491" />
            </Box>
          </svg>
          <Box
            component="svg"
            className="absolute -top-64 -right-64 opacity-20"
            sx={{ color: "primary.light" }}
            viewBox="0 0 220 192"
            width="220px"
            height="192px"
            fill="none"
          >
            <defs>
              <pattern
                id="837c3e70-6c3a-44e6-8854-cc48c737b659"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect x="0" y="0" width="4" height="4" fill="currentColor" />
              </pattern>
            </defs>
            <rect
              width="220"
              height="192"
              fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)"
            />
          </Box>

          <div className="relative z-10 w-full max-w-2xl">
            <div className="font-bold leading-none text-7xl text-grey-800">
              <div>Welcome to</div>
              <div>MPMedia!</div>
            </div>
            <div className="mt-24 text-lg leading-6 tracking-tight text-grey-800">
              Powerful and intuitive all-in-one monitoring console for remote
              signage devices.
            </div>
          </div>
        </Box>
      </Paper>
    </div>
  );
}

export default SignInPage;
